import React from 'react';
import { motion } from 'framer-motion';
import { timeFormat } from 'd3-time-format';
import { Group } from '@visx/group';
import { Icon } from '../common/Icon';

const MAX_TIME_DIFF = 7 * 1000; // seconds in ms
const formatDate = timeFormat('%H:%M');
const getSensorDate = (d) => {
  if (d.date) {
    return d.date;
  }
  if (d.timestamp) {
    return new Date(d.timestamp);
  }
};
const getSensorValue = (d) => {
  let val = 0;
  if (d.value) {
    val = d.value;
  }
  if (d.rank) {
    val = d.rank;
  }

  return val === 0 ? 0 : (Math.round(val * 100) / 100).toFixed(2);
};

const getDynLimLoBound = (d) => {
  let loBound = 0;

  if (d.loLimit) {
    loBound = d.loLimit;
  }

  return loBound === 0 ? 0 : (Math.round(loBound * 100) / 100).toFixed(2);
};
const getDynLimHiBound = (d) => {
  let hiBound = 0;
  if (d.hiLimit) {
    hiBound = d.hiLimit;
  }
  return hiBound === 0 ? 0 : (Math.round(hiBound * 100) / 100).toFixed(2);
};

const spring = {
  type: 'spring',
  stiffness: 500,
  damping: 50,
};

const TrendTooltip = ({ data, left, top, unit = '', dynLimData }) => {
  // hide tooltip if the gap between sensor and dynamic limit data is too big
  let showDynLim = false;
  if (dynLimData && typeof dynLimData === 'object' && dynLimData !== undefined) {
    const sensorDate = getSensorDate(data);
    const dynLimDate = getSensorDate(dynLimData);
    if (
      typeof sensorDate === 'number' &&
      typeof dynLimDate === 'number' &&
      Math.abs(sensorDate - dynLimDate) <= MAX_TIME_DIFF
    ) {
      showDynLim = true;
    }
  }

  return (
    <motion.div
      className="trend__tooltip"
      initial={{
        x: left,
        y: top,
        opacity: 0,
      }}
      animate={{
        x: left,
        y: top,
        opacity: 1,
      }}
      transition={spring}
    >
      <div className="trend__tooltip-content">
        <div className="trend__tooltip-time">
          <Icon name="icon-history" className="trend__tooltip-icon" />
          <div>{formatDate(getSensorDate(data))}</div>
        </div>
        <div className="trend__tooltip-separator" />
        <div className="trend__tooltip-unit">
          <Icon name="cra-dot" className="trend__tooltip-icon" id="trend__tooltip-value-icon" />
          <div>
            <strong>{getSensorValue(data)}</strong> {unit}
          </div>
        </div>
        {showDynLim ? (
          <div className="trend__tooltip-dyn-limits">
            <div className="trend__tooltip-unit trend__tooltip-dyn">
              <Icon
                name="cra-limit-high"
                className="trend__tooltip-icon"
                id="trend__tooltip-dyn-icon"
              />
              <div>
                <strong>{`${getDynLimHiBound(dynLimData)}`}</strong> <span className="trend__tooltip-dyn-label">DYN H</span>
              </div>
            </div>
            <div className="trend__tooltip-unit trend__tooltip-dyn">
              <Icon
                name="cra-limit-low"
                className="trend__tooltip-icon"
                id="trend__tooltip-dyn-icon"
              />
              <div>
                <strong>{`${getDynLimLoBound(dynLimData)}`}</strong> <span className="trend__tooltip-dyn-label">DYN L</span>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </motion.div>
  );
};

export const LineAndDot = ({ height, left, top }) => {
  return (
    <Group>
      <motion.line
        x1={0}
        y1={0}
        x2={0}
        y2={height}
        transition={spring}
        initial={{
          x: left,
          opacity: 0,
        }}
        animate={{
          x: left,
          opacity: 1,
        }}
        stroke="currentColor"
        strokeOpacity="0.5"
        strokeWidth="1px"
        pointerEvents="none"
      />
      <motion.circle
        cx={0}
        cy={0}
        transition={spring}
        initial={{
          x: left,
          y: top,
          opacity: 0,
        }}
        animate={{
          x: left,
          y: top,
          opacity: 1,
        }}
        r={3}
        fill="var(--color-observations)"
        pointerEvents="none"
      />
    </Group>
  );
};

export default TrendTooltip;
